import type { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import cx from 'classnames';
import { subDays } from 'date-fns';

import { getFormatterOverrides } from '@trello/dates/i18n';
import { currentLocale } from '@trello/locale';
import { WarningIcon } from '@trello/nachos/icons/warning';

import styles from './PowerUpListingOverview.less';

export interface PowerUpListingOverviewProps {
  overview: string;
  deprecation?: {
    sunsetDate: string | null;
    infoLink: string | null;
  };
  size?: 'medium' | 'small';
}

export const PowerUpListingOverview: FunctionComponent<
  PowerUpListingOverviewProps
> = ({ overview, deprecation, size = 'medium' }) => {
  const { sunsetDate, infoLink } = deprecation ?? {};

  if (!sunsetDate) {
    return (
      <span
        className={cx({
          [styles.clampedOverview]: size === 'medium',
          [styles.clampedOverviewSmall]: size === 'small',
        })}
      >
        {overview}
      </span>
    );
  }

  const customFormatter = new Intl.DateTimeFormat(currentLocale, {
    dateStyle: 'long',
    timeZone: 'UTC',
    ...getFormatterOverrides(),
  });

  return (
    <span
      className={cx({
        [styles.overview]: size === 'medium',
        [styles.overviewSmall]: size === 'small',
      })}
    >
      <WarningIcon size="small" />
      <span>
        <FormattedMessage
          id="templates.plugin_deprecation.plugin-overview"
          defaultMessage="This Power-Up will be deprecated after {sunsetDate}."
          description="Power-Up deprecation date warning"
          values={{
            sunsetDate: customFormatter.format(
              subDays(new Date(sunsetDate), 1),
            ),
          }}
        />
        {infoLink && (
          <>
            {' '}
            <a target="_blank" href={infoLink}>
              <FormattedMessage
                id="templates.plugin_deprecation.learn-more"
                defaultMessage="Learn more"
                description="Learn more about Power-Up deprecation"
              />
            </a>
          </>
        )}
      </span>
    </span>
  );
};
